import type {Announcements} from '@dnd-kit/core';

import {getItemAccessibleName, useItemAccessibleName} from '../../shared/accessibleName';
import type {DragProgress} from '../../shared/dragProgress';
import type {ItemMoveCopy} from '../../shared/itemMoveCopy';
import {useItemMoveCopy} from '../../shared/itemMoveCopy';
import type {DropSlot, WordBankItem} from '../store';
import {WORD_BANK_ID} from '../store';

const itemName = (copy: ItemMoveCopy, item: WordBankItem): string =>
  getItemAccessibleName(item.value) || copy.unnamedItem;

/** The spoken name of a blank: the prompt it answers, or its number when it has none. */
export const blankName = (copy: ItemMoveCopy, promptName: string, index: number): string =>
  promptName || copy.blank(index + 1);

/** `blankName` for a rendered blank, following its prompt as a supplement in it loads. */
export const useBlankName = (label: string | undefined, index: number): string =>
  blankName(useItemMoveCopy(), useItemAccessibleName(label), index);

const blankNameFor = (copy: ItemMoveCopy, dropSlots: DropSlot[], slotId: string): string => {
  const index = dropSlots.findIndex((slot) => slot.id === slotId);
  return index === -1
    ? copy.unknownBlank
    : blankName(copy, getItemAccessibleName(dropSlots[index].label ?? ''), index);
};

export const selectedMessage = (copy: ItemMoveCopy, item: WordBankItem): string =>
  copy.selectedForBlank(itemName(copy, item));

export const deselectedMessage = (copy: ItemMoveCopy, item: WordBankItem): string =>
  copy.deselected(itemName(copy, item));

export const backWhereItWasMessage = (copy: ItemMoveCopy, item: WordBankItem): string =>
  copy.backWhereItWas(itemName(copy, item));

/**
 * What `placeItemInSlot(itemId, targetId)` does, described from the state before it ran: the
 * item lands in the blank or goes back to the bank, and whatever held the blank returns to the bank.
 */
export const placementMessage = (
  copy: ItemMoveCopy,
  wordBank: WordBankItem[],
  dropSlots: DropSlot[],
  itemId: string,
  targetId: string
): string => {
  const item = wordBank.find((candidate) => candidate.id === itemId);
  if (!item) return '';
  const name = itemName(copy, item);

  if (targetId === WORD_BANK_ID) {
    return item.placedInSlot ? copy.returnedToBank(name) : backWhereItWasMessage(copy, item);
  }
  if (item.placedInSlot === targetId) return backWhereItWasMessage(copy, item);

  const displaced = wordBank.find(
    (candidate) => candidate.id !== itemId && candidate.placedInSlot === targetId
  );
  const placed = copy.placed(name, blankNameFor(copy, dropSlots, targetId));
  return displaced ? `${placed} ${copy.returnedToBank(itemName(copy, displaced))}` : placed;
};

/**
 * dnd-kit announcements for the item bank, spoken through DndContext's own live region. They
 * read the bank and blanks as rendered: DndContext runs our drop handler before it announces,
 * so on drop the store has already placed the item, but the pre-drop state describes the same
 * move.
 */
export const itemBankAnnouncements = (
  copy: ItemMoveCopy,
  wordBank: WordBankItem[],
  dropSlots: DropSlot[],
  progress: DragProgress = {hasLeftOrigin: false}
): Announcements => {
  const find = (id: string | number): WordBankItem | undefined =>
    wordBank.find((item) => item.id === id);

  return {
    onDragStart: ({active}) => {
      const item = find(active.id);
      if (!item) return undefined;
      progress.hasLeftOrigin = false;
      return selectedMessage(copy, item);
    },
    onDragOver: ({active, over}) => {
      const item = find(active.id);
      if (!item) return undefined;
      const origin = item.placedInSlot ?? WORD_BANK_ID;
      if (over?.id === origin && !progress.hasLeftOrigin) return undefined;
      progress.hasLeftOrigin = true;
      if (!over) return copy.notOverBlank;
      if (over.id === WORD_BANK_ID) return copy.overBank;
      return copy.overBlank(blankNameFor(copy, dropSlots, over.id.toString()));
    },
    onDragEnd: ({active, over}) => {
      const item = find(active.id);
      if (!item) return undefined;
      if (!over || over.id === active.id) return backWhereItWasMessage(copy, item);
      return placementMessage(copy, wordBank, dropSlots, item.id, over.id.toString());
    },
    onDragCancel: ({active}) => {
      const item = find(active.id);
      return item ? backWhereItWasMessage(copy, item) : undefined;
    }
  };
};
